import * as React from 'react'
import { Children, useEffect, useState } from 'react'
import { clsx } from 'clsx'

import { Flex } from '../Flex'
import { Badge } from '../Badge'
import { mapAvatarStackSizeToBadgeSize } from '../Badge/mapAvatarStackSizeToBadgeSize'
import { VisuallyHidden } from '../VisuallyHidden'

import styles from './Avatar.module.css'

// TODO: replace with SVG
const DEFAULT_AVATAR = `https://s3.amazonaws.com/buffer-ui/Default+Avatar.png`

type AvatarSize =
  | 'xxsmall'
  | 'xsmall'
  | 'small'
  | 'mid'
  | 'medium'
  | 'large'
  | 'xlarge'

type AvatarFallback = 'image' | 'letter'

const LETTER_COLOR_COUNT = 5

const LETTER_COLOR_CLASSES = [
  styles.color0,
  styles.color1,
  styles.color2,
  styles.color3,
  styles.color4,
] as const

/**
 * Deterministically map a string to one of `LETTER_COLOR_COUNT` color slots.
 * Same input always yields the same slot, so a given user keeps the same color.
 */
function getLetterColorIndex(seed: string): number {
  const hash = seed.split('').reduce((acc, char) => {
    return char.charCodeAt(0) + ((acc << 5) - acc)
  }, 0)
  return Math.abs(hash) % LETTER_COLOR_COUNT
}

// The legacy `Default+Avatar` URL is what publishing/community APIs return when
// a user has no real picture, so consumers passing it in should also flip to letter.
function isPlaceholderSrc(src: string | undefined): boolean {
  if (!src) return true
  return src.includes('Default+Avatar.png')
}

interface AvatarProps extends Omit<
  React.HTMLProps<HTMLImageElement>,
  'size' | 'src' | 'alt'
> {
  /**
   * Size of the avatar
   * @default medium
   */
  size?: AvatarSize
  /**
   * Source of the avatar image
   */
  src?: string
  /**
   * Alt text for the avatar image, pass `""` if the avatar is purely decorative
   */
  alt: string
  /**
   * Fallback when `src` is missing or fails to load.
   * `image` shows the default PNG; `letter` shows a letter on a brand color.
   * @default image
   */
  fallback?: AvatarFallback
  /**
   * Seed for the letter and color when `fallback="letter"`. Defaults to `alt`.
   */
  fallbackName?: string
  /**
   * Override the derived letter. Defaults to the first character of `fallbackName ?? alt`.
   */
  fallbackLetter?: string
}

const AvatarRoot = React.forwardRef<HTMLImageElement, AvatarProps>(
  (
    {
      size = 'medium',
      className,
      src,
      alt,
      fallback = 'image',
      fallbackName,
      fallbackLetter,
      ...props
    }: AvatarProps,
    forwardedRef,
  ) => {
    const [innerSrc, setInnerSrc] = useState(src)
    const [hasErrored, setHasErrored] = useState(false)

    useEffect(() => {
      setInnerSrc(src)
      setHasErrored(false)
    }, [src])

    const trimmedAlt = alt.trim()
    const trimmedSeed = (fallbackName ?? alt).trim()
    const shouldRenderLetter =
      fallback === 'letter' &&
      trimmedSeed !== '' &&
      (hasErrored || isPlaceholderSrc(innerSrc))

    if (shouldRenderLetter) {
      const trimmedFallbackLetter = fallbackLetter?.trim() ?? ''
      const letter =
        trimmedFallbackLetter || trimmedSeed.charAt(0).toUpperCase()
      const colorIndex = getLetterColorIndex(trimmedSeed)
      const isDecorative = trimmedAlt === ''

      return (
        <div
          className={clsx(
            styles.picture,
            styles[size],
            styles.letter,
            LETTER_COLOR_CLASSES[colorIndex],
            className,
          )}
          role={isDecorative ? undefined : 'img'}
          aria-label={isDecorative ? undefined : trimmedAlt}
          aria-hidden={isDecorative || undefined}
        >
          {letter}
        </div>
      )
    }

    return (
      <picture className={clsx(styles.picture, styles[size])}>
        <img
          ref={forwardedRef}
          className={clsx(styles.avatar, className)}
          src={innerSrc ?? DEFAULT_AVATAR}
          alt={alt}
          onError={() => {
            // Blank seed → no letter to render; recover to the default PNG.
            if (fallback === 'letter' && trimmedSeed !== '') {
              setHasErrored(true)
            } else {
              setInnerSrc(DEFAULT_AVATAR)
            }
          }}
          {...props}
        />
      </picture>
    )
  },
)
AvatarRoot.displayName = 'Avatar'

export interface AvatarStackProps extends React.ComponentPropsWithoutRef<'div'> {
  /**
   * Whether the avatar should be on top or on bottom of the previous avatar
   * @default lastOnTop
   */
  stacking?: 'lastOnTop' | 'firstOnTop'
  /**
   * Number of max avatars to show in the stack
   */
  max?: number
  /**
   * Size of the avatar stack
   * @default medium
   */
  size?: AvatarSize
}

interface AvatarStackCssProperties extends React.CSSProperties {
  '--avatar-stack-z-index': string
}

function getAvatarStyle(index: number): AvatarStackCssProperties {
  return {
    '--avatar-stack-z-index': index.toString(),
  }
}

/**
 * AvatarStack is a component used to display avatar groups, by stacking avatars
 * on top of each other. It also displays a badge with the count of remaining
 * avatars that are not visible.
 *
 * You can customize the size of the avatar stack with the following CSS variables:
 *
 * `--avatar-stack-border-width` to change the border width of the avatars
 * `--avatar-stack-margin-left` to change the margin left of the avatars
 * `--avatar-stack-border-color` to change the border color of the avatars
 */
const AvatarStack = React.forwardRef<HTMLDivElement, AvatarStackProps>(
  (
    {
      children: _children,
      stacking = 'lastOnTop',
      max,
      size = 'medium',
      style,
      className,
      ...props
    }: AvatarStackProps,
    forwardedRef,
  ) => {
    const childrenArray = Children.toArray(_children)
    const remaining = Math.max(max ? childrenArray.length - max : 0, 0)

    const children = childrenArray.slice(0, max)

    const calculateZIndex = (index: number) => {
      if (stacking === 'firstOnTop') {
        return (max ?? childrenArray.length) - index
      }
      return index + 1
    }

    return (
      <Flex
        align="center"
        className={clsx(styles[size], styles.stack, className)}
        style={style}
        ref={forwardedRef}
        {...props}
      >
        {children.map((child, index) => {
          return (
            <div
              key={index}
              className={styles.avatarWrapper}
              style={getAvatarStyle(calculateZIndex(index))}
            >
              {child}
            </div>
          )
        })}
        {remaining > 0 && (
          <Badge
            className={styles.badge}
            style={getAvatarStyle(calculateZIndex(children.length))}
            size={mapAvatarStackSizeToBadgeSize(size)}
          >
            +{remaining}
            <VisuallyHidden>more avatars</VisuallyHidden>
          </Badge>
        )}
      </Flex>
    )
  },
)
AvatarStack.displayName = 'AvatarStack'

const Avatar = Object.assign(AvatarRoot, {
  Stack: AvatarStack,
})
export { Avatar }
export type { AvatarProps, AvatarSize, AvatarFallback }
